import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useTranslation } from '@unifyapps/i18n/client';
import SvgDownload01 from '@unifyapps/icons/outline/Download01';
import { iconSizeVariants } from '@unifyapps/ui/components/Button/styles';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import { TypographyWithTooltip } from '@unifyapps/ui/components/Typography/TypographyWithTooltip';
import clsx from 'clsx';
import useMutationHelpers from '../../../hooks/useMutationHelpers';
import useFetchPlatformAutomationConfig, {
  PlatformAutomationConfigType,
} from '../../../platform-automation/hooks/useFetchPlatformAutomationConfig';
import useExecuteNode from '../../Copilot/hooks/useExecuteNode';
import { useMarkdownContext } from '../context/MarkdownContext';
import type { MarkdownTableColumnSize } from '../utils/tableColumnSizingUtil';

interface Props {
  children: React.ReactNode;
  ref?: React.Ref<HTMLTableCellElement>;
  getExportParams?: () => Record<string, unknown>;
  className?: string;
  columnSize?: MarkdownTableColumnSize;
}

export function TableHeaderCell({ children, ref, getExportParams, className, columnSize }: Props) {
  const { t } = useTranslation(['common']);
  const { markdown } = useMarkdownContext();
  const { mutate, isPending } = useExecuteNode();
  const { config } = useFetchPlatformAutomationConfig(PlatformAutomationConfigType.MarkdownToCSV, {
    staleTime: Infinity,
  });
  const { onMutationError, onShowErrorSnackbar } = useMutationHelpers();

  const handleClick = useEventCallback(() => {
    mutate(
      {
        automationId: config?.automationId,
        synchronous: true,
        parameters: { markdownText: markdown, ...getExportParams?.() },
      },
      {
        onSuccess: (payload: { response?: { fileURL: string } }) => {
          if (!payload.response?.fileURL) {
            onShowErrorSnackbar();
            return;
          }
          window.open(payload.response?.fileURL, '_self');
        },
        onError: onMutationError,
      },
    );
  });

  return (
    <th
      className={clsx('group/th relative !border-b !align-middle', className)}
      data-col-size={columnSize}
      ref={ref}
    >
      {/* in the last header cell to avoid the header title  to overlap with the download button we have provided this class */}
      <TypographyWithTooltip
        className="[th:last-child_&]:block [th:last-child_&]:pe-5xl"
        maxNumberOfLines={4}
      >
        {children}
      </TypographyWithTooltip>

      {/* Only visible on last th using parent selector */}
      <Tooltip placement="bottom" title={t('common:Actions.Download')}>
        <IconButton
          // download button is visble at the end of the header
          className="absolute top-1/2 end-xxs -translate-y-1/2 hidden [th:last-child_&]:inline-flex"
          color="neutral"
          IconNode={<SvgDownload01 className={iconSizeVariants({ size: 'md' })} strokeWidth={2} />}
          loading={isPending}
          onClick={handleClick}
          size="sm"
          variant="soft"
        />
      </Tooltip>
    </th>
  );
}
